// ============================================================
// CombatantStatusTab - the merged Body Map + Conditions tab.
//
// LAYOUT ONLY. Every panel-specific piece arrives as a slot, so there
// is no `if (side === …)` anywhere: the actor panel passes its plate,
// a trait+talent row and its chips; the target panel passes the
// interactive map with armour numerals, a trait row and the conditions.
//
// One column (plan plate-unboxed: the rails beside the map became rows
// under the health bar, so the figure has the whole width):
//
//   1fr     the map takes all spare HEIGHT
//   auto    turn strip
//   auto    wounds (the health bar)
//   auto    traits (and the actor's talents) - not shown when empty
//   auto    conditions - not shown when empty
//   auto    footer (the Conditions block and the rest)
//
// `min-height: 0` on the tab and the map cell is the load-bearing line.
// Flex and grid children default to min-height:auto and refuse to shrink
// below their content, so without it the map overflows the panel instead
// of fitting inside it - which is exactly how a hard-coded maxWidth:320
// ended up being the only thing keeping it in bounds.
// ============================================================
import type { ReactNode } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/CombatantStatusTab.module.css';

export interface CombatantStatusTabProps {
  bodyMap: ReactNode;
  /** The traits (and the actor's talents) as a row under the health bar; renders nothing when there are none. */
  traitsRow?: ReactNode;
  /** The conditions as a row under the traits; renders nothing when there are none. */
  conditionsRow?: ReactNode;
  /**
   * The turn state (ACTING NOW / round position + reroll) as its own
   * slim row between map and wounds (v8.246.0) - it used to float
   * over the map and collided with the target's sights.
   */
  turnStrip?: ReactNode;
  wounds?: ReactNode;
  footer?: ReactNode;
  /**
   * Decoration layered over the map cell (v8.244.0) - the target
   * panel's frame brackets. Must be pointer-events: none inside.
   */
  mapOverlay?: ReactNode;
  className?: string;
}

export default function CombatantStatusTab({
  bodyMap,
  traitsRow,
  conditionsRow,
  turnStrip,
  wounds,
  footer,
  mapOverlay,
  className,
}: CombatantStatusTabProps) {
  return (
    <div className={clsx(styles.statusTab, className)} data-testid="status-tab">
      <div className={styles.mapCell}>
        {bodyMap}
        {mapOverlay}
      </div>
      {turnStrip && <div className={styles.turnStrip}>{turnStrip}</div>}
      {wounds && <div className={styles.wounds}>{wounds}</div>}
      {traitsRow && <div className={styles.traitsRow}>{traitsRow}</div>}
      {conditionsRow && <div className={styles.conditionsRow}>{conditionsRow}</div>}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}
